<template>
    <div v-if="waiver.canbeSigned">

      <h2 class="text-center">
        <p>
          {{
            $t("memberRegistration.step_waiver.content.step_header_prefix")
          }}
          {{ waiver.participantName }} {{ waiver.participantLastname }}
        </p>
      </h2>
      <h2 class="text-center">{{waiver.displayName }}</h2>

      {{ /** DOCUMENT MESSAGE */ }}
      <v-card outlined>
        <v-card-text class="text-start">
          <div v-html="waiver.documentMessage"></div>
          <v-spacer />
          <v-checkbox :disabled="waiver.isAgreedChk != null" v-model="waiver.isAgreedChk" :label="agreedChkLbl"></v-checkbox>
        </v-card-text>
      </v-card>

      <br />

      {{ /** Participant Information */ }}
      <v-card outlined>
        <v-card-title>
          <div>{{ isMinor(waiver.participantDateOfBirth) ? $t("member_registration.waiver_participant.minor_information") : $t("member_registration.waiver_participant.information") }}</div>
        </v-card-title>
        <v-card-text class="text-start">
          <div>
            <span>{{ $t("member_registration.waiver_participant.name") }}: </span>
            <span class="font-weight-bold">{{ waiver.participantName }} {{ waiver.participantLastname }}</span>
          </div>
          <div v-if="showWaiverAmaNumber">
            <span>AMA Number: </span>
            <span class="font-weight-bold">{{ waiver.validAmaNumber }}</span>
          </div>
          <div>
            <span>{{ $t("member_registration.waiver_participant.email") }}: </span>
            <span class="font-weight-bold">{{ waiver.participantEmail }}</span>
          </div>
          <div>
            <span>{{ $t("member_registration.waiver_participant.gender") }}: </span>
            <span class="font-weight-bold">{{ translateGender(waiver.participantGender) }}</span>
          </div>
          <div>
            <span>{{ $t("member_registration.waiver_participant.dob") }}: </span>
            <span class="font-weight-bold">{{ waiver.participantDateOfBirth | formatDateForHumans(false) }}</span>
          </div>
          <div v-if="participantFormattedAddress">
            <span>{{ $t("common.address") }}: </span>
            <span class="font-weight-bold">{{ participantFormattedAddress }}</span>
          </div>
          <template v-if="!isMinor(waiver.participantDateOfBirth)">
            <v-spacer />
            <v-checkbox :disabled="waiver.isCertifyChk != null" v-model="waiver.isCertifyChk" :label="certifyChkLbl"></v-checkbox>
          </template>
        </v-card-text>
      </v-card>

      <br v-if="isMinor(waiver.participantDateOfBirth)" />

      {{ /** Parent/Guardian Information - shown only for minors */ }}
      <v-card outlined v-if="isMinor(waiver.participantDateOfBirth)">
        <v-card-title>
          <div>{{ $t("member_registration.waiver_participant.signatory_information") }}</div>
        </v-card-title>
        <v-card-text class="text-start">
          <div>
            <span>{{ $t("member_registration.waiver_participant.name") }}: </span>
            <span class="font-weight-bold">{{ waiver.signatoryName }} {{ waiver.signatoryLastname }}</span>
          </div>
          <div>
            <span>{{ $t("member_registration.waiver_participant.email") }}: </span>
            <span class="font-weight-bold">{{ waiver.signatoryEmail }}</span>
          </div>
          <div>
            <span>{{ $t("member_registration.waiver_participant.gender") }}: </span>
            <span class="font-weight-bold">{{ translateGender(waiver.signatoryGender) }}</span>
          </div>
          <div>
            <span>{{ $t("member_registration.waiver_participant.dob") }}: </span>
            <span class="font-weight-bold">{{ waiver.signatoryDateOfBirth | formatDateForHumans(false) }}</span>
          </div>
          <div v-if="signatoryFormattedAddress">
            <span>{{ $t("common.address") }}: </span>
            <span class="font-weight-bold">{{ signatoryFormattedAddress }}</span>
          </div>
          <v-spacer />
          <v-checkbox :disabled="waiver.isCertifyChk != null" v-model="waiver.isCertifyChk" :label="certifyChkLbl"></v-checkbox>
        </v-card-text>
      </v-card>

      <br />

      {{ /** Participant Signature */ }}
      <v-card outlined>
        <v-card-title>
          <div>
            {{ hasAdultWaiver ? $t("member_registration.waiver_participant.racers_signature_adult") : $t("member_registration.waiver_participant.racers_signature_minor") }}
          </div>
        </v-card-title>
        <v-card-text class="text-start">
          <v-row class="align-center" v-if="signature.signatureUUID && signature.signatureImage">
                <v-col md="11" sm="11" cols="12" class="text-left">
                  <v-img max-height="200" max-width="700" :src="getSignatureImage(signature)"></v-img>
                </v-col>
          </v-row>
          <v-dialog v-model="dialog" max-width="750">
            <template v-slot:activator="{ on, attrs }">
              <v-btn color="primary" dark v-bind="attrs" v-on="on" @click="getSignatures">
                {{ $t("member_registration.waiver_participant.sign_now") }}
                <v-icon style="padding-left:10px">fa fas fa-signature</v-icon>
              </v-btn>
            </template>
            <StepWaiverSignature 
              ref="signaturesRef"
              :waiver="waiver" 
              :signatures="signatures" 
              :urlDomain="urlDomain"
              :loadingSignatures="loadingSignatures" 
              :dialog="dialog" 
              @setSignature="setSignature"
              @getSignatures="getSignatures"
              @closeDialog="closeDialog" />
          </v-dialog>
        </v-card-text>
      </v-card>

      <br />

      {{ /** Electronic Signature Consent */ }}
      <v-card outlined>
        <v-card-title>
          <div>{{ $t("member_registration.waiver_participant.electronic_signature_consent") }}</div>
        </v-card-title>
        <v-card-text class="text-start">
          <div v-html="waiver.electronicConsent"></div>
          <v-spacer />
          <v-checkbox :disabled="waiver.isElectronicConsentChk != null" v-model="waiver.isElectronicConsentChk" :label="electronicConsentChkLbl"></v-checkbox>
        </v-card-text>
      </v-card>

      <v-card outlined>
        <v-card-actions>
            <v-btn
            class="white--text"
            :color="butonColor"
            @click="rejectWaiver"
            >
            {{$t("eventWaiver.reject")}}
            </v-btn>
            <v-btn
            :color="butonColor"
            @click="signDocument"
            class="white--text"
            :disabled="isSingDisabled"
            >
            {{$t("eventWaiver.signed")}}
            </v-btn>
        </v-card-actions>
      </v-card>

      <v-dialog v-model="dialogDelete" max-width="500px">
        <v-card>
          <v-card-title class="text-h5">{{  $t("member_registration.waiver_required") }}</v-card-title>
          <v-card-text class="text-start">
            <div>{{ $t("member_registration.waiver_required_description") }}</div>
            <div>{{ $t("member_registration.waiver_required_description_2") }}</div>
          </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
            <v-btn :color="butonColor" class="white--text" @click="closeDialogDelete">{{
              $t("member_registration.go_back")
            }}</v-btn>
            <v-btn
              :color="butonColor"
              class="white--text"
              @click="rejectAndCancel"
              >{{ $t("member_registration.reject_and_cancel") }}</v-btn
            >
            <v-spacer></v-spacer>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
</template>

<script>
import APIService from "@/services/APIService";
import StepWaiverSignature from "@/components/memberRegistration/Step_WaiverSignature.vue";
import { mapGetters } from "vuex";

import {
  VDialog,
  VCard,
  VCardTitle,
  VCardText,
  VCardActions,
  VSpacer,
  VCheckbox,
  VBtn, VIcon, VRow, VCol, VImg
} from "vuetify/lib";

export default {
  components: {
    VDialog,
    VCard,
    VCardTitle,
    VCardText,
    VCardActions,
    VSpacer,
    VCheckbox,
    StepWaiverSignature,
    VBtn, VIcon, VRow, VCol, VImg
  },
  props: {
    i: Number,
    waiver: Object,
    membershipType: Object,
    p: Object,
    userId: Number,
    organizationId: Number,
    urlDomain: String,
    numWaiversToSign: Number,
    previousStep: Number
  },
  data() {
    return {
      dialog: false,
      loadingSignatures: false,
      signatures: null,
      checkbox: {
        isAgreedChk: {
          status: false,
          updatedAt: null
        },
        isCertifyChk: {
          status: false,
          updatedAt: null
        },
        isElectronicConsentChk: {
          status: false,
          updatedAt: null
        },
      },
      signature: {
        signatureUUID: null,
        signatureImage: null
      },
      defaultSignature: null,
      dialogDelete: false,
    };
  },
  watch: {
    isAgreedChk() {
      if(this.loadingIsAgreedChk)
      { 
        this.loadingIsAgreedChk = false;
        return;
      }
      const timesStamp = this.getTimeStamp();
      this.updateChecks('isAgreedChk', timesStamp)
      this.checkbox.isAgreedChk.updatedAt = timesStamp;
    },
    isCertifyChk() {
      if(this.loadingIsCertifyChk)
      { 
        this.loadingIsCertifyChk = false;
        return;
      }

      const timesStamp = this.getTimeStamp();
      this.updateChecks('isCertifyChk', timesStamp)
      this.checkbox.isCertifyChk.updatedAt = timesStamp;
    },
    isElectronicConsentChk() {
      if(this.loadingIsElectronicConsentChk)
      { 
        this.loadingIsElectronicConsentChk = false;
        return;
      }

      const timesStamp = this.getTimeStamp();
      this.updateChecks('isElectronicConsentChk', timesStamp)
      this.checkbox.isElectronicConsentChk.updatedAt = timesStamp;
    },
    waiver(val) {
      if(!val) return;
      // New waiver handed in — scrub any stale pre-sign check
      // timestamps so the UI starts fresh for anything not actually
      // signed (see resetStalePreSignChecks).
      this.resetStalePreSignChecks();

      this.checkbox = {
        isAgreedChk: { status: false, updatedAt: null },
        isCertifyChk: { status: false, updatedAt: null },
        isElectronicConsentChk: { status: false, updatedAt: null },
      };
      this.signature = { signatureUUID: null, signatureImage: null };

      if(val.isAgreedChk) {
        this.loadingIsAgreedChk = true;
        this.checkbox.isAgreedChk.status = true;
        this.checkbox.isAgreedChk.updatedAt = val.isAgreedChk;
      }

      if(val.isCertifyChk) {
        this.loadingIsCertifyChk = true;
        this.checkbox.isCertifyChk.status = true;
        this.checkbox.isCertifyChk.updatedAt = val.isCertifyChk;
      }

      if(val.isElectronicConsentChk) {
        this.loadingIsElectronicConsentChk = true;
        this.checkbox.isElectronicConsentChk.status = true;
        this.checkbox.isElectronicConsentChk.updatedAt = val.isElectronicConsentChk;
      }

    }
  },
  computed: {
    ...mapGetters({
      env: "env",
    }),
    butonColor() {
      return this.env.get("VUE_APP_BUTTON_COLOR");
    },
    hasAdultWaiver() {
        const birthDate = new Date(this.waiver.participantDateOfBirth);
        const today = new Date();
        const age = today.getFullYear() - birthDate.getFullYear();

        const monthDifference = today.getMonth() - birthDate.getMonth();
        const dayDifference = today.getDate() - birthDate.getDate();
        if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
            return age - 1 >= 18;
        }
        return age >= 18;
    },
    langCode() {
      let code;
      switch (this.$i18n.locale) {
        case "fr":
          code = 2;
          break;

        case "es":
          code = 3;
          break;

        default:
          code = 1;
          break;
      }
      return code;
    },
    isAgreedChk() {
      return this.waiver.isAgreedChk;
    },
    isCertifyChk() {
      return this.waiver.isCertifyChk;
    },
    isElectronicConsentChk() {
      return this.waiver.isElectronicConsentChk;
    },
    showWaiverAmaNumber() {
      const memberType = this.waiver && this.waiver.organizationMemberType
        ? this.waiver.organizationMemberType
        : null;
      const requiresAma = Number((memberType && (memberType.isRequireAMA || memberType.requiresValidAma)) || 0) === 1;
      return requiresAma && !!(this.waiver && this.waiver.validAmaNumber);
    },
    participantFormattedAddress() {
      const parts = [
        this.waiver.participantAddress,
        this.waiver.participantCity,
        this.waiver.participantState,
        this.waiver.participantCountry,
        this.waiver.participantZipPostalCode,
      ].filter(Boolean);
      return parts.join(', ');
    },
    signatoryFormattedAddress() {
      const parts = [
        this.waiver.signatoryAddress,
        this.waiver.signatoryCity,
        this.waiver.signatoryState,
        this.waiver.signatoryCountry,
        this.waiver.signatoryZipPostalCode,
      ].filter(Boolean);
      return parts.join(', ');
    },

    agreedChkLbl() {
      let response = this.$t("member_registration.waiver_participant.i_agree");

      if (this.checkbox.isAgreedChk.updatedAt) {
        response += ` (${this.$options.filters.formatDateForHumans(this.checkbox.isAgreedChk.updatedAt,true)})`
      }
      return response;
    },
    certifyChkLbl() {
      if (this.isMinor(this.waiver.participantDateOfBirth)) {
        const time = this.checkbox.isCertifyChk.updatedAt
          ? this.$options.filters.formatDateForHumans(this.checkbox.isCertifyChk.updatedAt, true)
          : '';
        // Personalized declaration when a relationship was captured by the
        // ParentGuardianDialog — embeds the signatory's legal relationship
        // and the minor's name so the statement is explicit. The
        // relationship code is persisted on the signed waiver row
        // (organizationWaiverSigned.signatoryRelationship), and fkUserId
        // resolves the minor, so audits can reproduce this sentence.
        const relCode = this.waiver.signatoryRelationship;
        if (relCode) {
          const minorName = `${this.waiver.participantName || ""} ${this.waiver.participantLastname || ""}`.trim();
          if (relCode === "Guardian") {
            return this.$t("member_registration.waiver_participant.guardian_consent_guardian", {
              minorName,
              time
            });
          }
          if (relCode === "Father" || relCode === "Mother") {
            const relKey = relCode === "Father"
              ? "memberRegistration.parent_guardian.father"
              : "memberRegistration.parent_guardian.mother";
            return this.$t("member_registration.waiver_participant.guardian_consent_parent", {
              relationship: this.$t(relKey),
              minorName,
              time
            });
          }
        }
        // Fallback (no relationship captured) — still personalize with the
        // minor's name so wording is consistent across all entry points.
        const minorName = `${this.waiver.participantName || ""} ${this.waiver.participantLastname || ""}`.trim();
        return this.$t("member_registration.waiver_participant.guardian_consent", { minorName, time });
      }
      let response = this.$t("member_registration.waiver_participant.i_certify");
      if (this.checkbox.isCertifyChk.updatedAt) {
        response += ` (${this.$options.filters.formatDateForHumans(this.checkbox.isCertifyChk.updatedAt, true)})`;
      }
      return response;
    },
    electronicConsentChkLbl() {
      let response = this.$t("member_registration.waiver_participant.i_agree");

      if (this.checkbox.isElectronicConsentChk.updatedAt) {
        response += ` (${this.$options.filters.formatDateForHumans(this.checkbox.isElectronicConsentChk.updatedAt,true)})`
      }
      return response;
    },
    isWaiverVisible() {
      return typeof this.p.waiver != "undefined" && this.isMinor(this.p.birthday) ||
              (!this.isMinor(this.p.birthday) && this.p.userId == this.userId);
    },
    isSingDisabled() {
      return !this.isAgreedChk ||
            !this.isCertifyChk ||
            !this.isElectronicConsentChk ||
            !this.signature.signatureUUID ||
            !this.signature.signatureImage ||
            !this.signature.signatureId ||
            this.waiver.waiverStatus == 'Signed';
    }
  },
  created() {
    this.getSignatures();
    this.resetStalePreSignChecks();
  },
  methods: {
    /**
     * updateOrganizationWaiverSigned persists each checkbox click
     * independently of signing, so a row can end up with
     * isAgreedChk / isCertifyChk / isElectronicConsentChk
     * timestamps while waiverStatus stays "NotSigned" and
     * fkUserSignatureId stays null. On re-render that looks like the
     * boxes were already ticked, with no way to uncheck because
     * :disabled="waiver.isXxxChk != null" greys them out.
     *
     * Reset those stale timestamps on the local waiver object so the
     * UI starts fresh for anything not actually signed. The backend
     * values get rewritten the moment the user re-checks a box
     * (updateChecks posts a new timestamp).
     */
    resetStalePreSignChecks() {
      const w = this.waiver;
      if (!w) return;
      if (w.waiverStatus === 'Signed') return;
      if (w.fkUserSignatureId) return;
      if (w.isAgreedChk) w.isAgreedChk = null;
      if (w.isCertifyChk) w.isCertifyChk = null;
      if (w.isElectronicConsentChk) w.isElectronicConsentChk = null;
    },
    translateGender(gender) {
      const rawValue = (gender || "").toString().trim();
      if (!rawValue) return rawValue;

      const normalized = rawValue.toLowerCase();
      const genderKeyMap = {
        male: "registration.male",
        m: "registration.male",
        masculino: "registration.male",
        hombre: "registration.male",
        homme: "registration.male",
        female: "registration.female",
        f: "registration.female",
        femenino: "registration.female",
        mujer: "registration.female",
        femme: "registration.female",
        other: "common.other",
        o: "common.other",
        otro: "common.other",
        autre: "common.other",
      };

      const key = genderKeyMap[normalized];
      return key ? this.$t(key) : rawValue;
    },
    getSignatureImage(signature) {
      return "data:image/png;base64," + signature.signatureImage;
    },
    setSignature(signature){
      this.signature = signature;
    },
    rejectAndCancel(){
      this.$emit('rejectAndCancel');
    },
    rejectWaiver(){
      this.dialogDelete = true;
    },
    closeDialogDelete(){
      this.dialogDelete = false;
    },
    async signDocument() {
      const path = `${this.urlDomain}/laravel/public/api/v1/participant/organization/waiver/sign`;
      // AMA Waiver Bypass v2: forward the relationship + sole custody flags
      // selected in ParentGuardianDialog / soleGuardian flow so the backend
      // can persist them onto organizationWaiverSigned and append the
      // canonical sole custody sentence.
      const data = {
        id: this.waiver.id,
        signatureId: this.signature.signatureId,
        signatoryRelationship: this.waiver.signatoryRelationship || null,
        isSoleCustody: !!this.waiver.isSoleCustody
      }

      await APIService.fetch({
        method: "post",
        path: path,
        data
      }).then((resp) => {
        if (resp.data.success)
        {
          this.$emit("closeWaiver");
          this.$emit('getOrganizationWaiverSigned');
        }
      });

    },
    isMinor(date) {
      //TODO: this could be made from laravel
      var today = new Date();
      var bd = new Date(date);
      var edad = today.getFullYear() - bd.getFullYear();
      var m = today.getMonth() - bd.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < bd.getDate())) {
        edad--;
      }
      return edad < 18;
    },

    validate() {
      if (!this.isAgreedChk ||
        !this.isCertifyChk ||
        !this.isElectronicConsentChk
      ) {
        return false;
      }

      return true;
    },
    async updateChecks(checkName, value)
    {
      const path = `${this.urlDomain}/laravel/public/api/v1/participant/organization/waiver/check`;
      const data = { checkName: checkName, value, id: this.waiver.id }

      await APIService.fetch({
        method: "post",
        path: path,
        data
      })
    },

    async getSignatures() {
      this.loadingSignatures = true;
      const path = `${this.urlDomain}/laravel/public/api/v1/participant/signatures`;
      await APIService.fetch({
        method: "get",
        path: path,
      })
        .then((resp) => {
          this.signatures = resp.data.success ? (resp.data.message || []) : [];
          this.loadingSignatures = false;
        })
        .catch(() => {
          this.signatures = [];
          this.loadingSignatures = false;
        });
    },

    closeDialog() {
      this.dialog = false;
    },

    getTimeStamp() {
      const now = new Date();
      let month = now.getMonth() + 1;
      month = month < 10 ? `0${month}` : month;
      let day = now.getDate() < 10 ? `0${now.getDate()}` : now.getDate();

      return `${now.getFullYear()}-${month}-${day} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
    },

  },
};
</script>
